'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useRef, useState } from 'react';
import { useCopyToClipboard } from '@uidotdev/usehooks';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react/ssr';
import { ResponsiveAlertDialog, ResponsiveAlertDialogAction, ResponsiveAlertDialogCancel, ResponsiveAlertDialogBody, ResponsiveAlertDialogContent, ResponsiveAlertDialogDescription, ResponsiveAlertDialogFooter, ResponsiveAlertDialogHeader, ResponsiveAlertDialogTitle } from '@/ui/responsive-alert-dialog';
import { Field, FieldLabel } from '@/ui/field';
import { Input } from '@/ui/input';
export const ConfirmContext = createContext(null);

// Imperative confirm(options): Promise<boolean> backed by a single
// AlertDialog instance mounted once at the root — call sites just
// `await confirm({...})` instead of each screen owning its own dialog
// open-state boilerplate for what's always the same "sure?" shape
// (destructive delete confirmations, drag/transfer guardrails). Passing
// `cancelLabel: null` drops the Cancel button, so the same plumbing also
// covers plain acknowledge-only messages (the former `window.alert` spots).
//
// `confirmText` (optional) turns this into a type-to-confirm dialog — the
// Confirm button stays disabled until the typed value matches (trimmed,
// case-insensitive), the standard guard for destructive deletes across the
// app: the entity's own name where one exists, or the word "eliminar" where
// it doesn't (e.g. leaving a workspace).
export const ConfirmProvider = t0 => {
  const $ = _c(44);
  const {
    children
  } = t0;
  const [state, setState] = useState(null);
  const [typedValue, setTypedValue] = useState("");
  const [copied, setCopied] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const $resolve = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = t2 => {
      const {
        title: t3,
        description,
        confirmLabel: t4,
        cancelLabel: t5,
        variant,
        confirmText
      } = t2 === undefined ? {} : t2;
      const title = t3 === undefined ? "\xBFEst\xE1s seguro?" : t3;
      const confirmLabel = t4 === undefined ? "Confirmar" : t4;
      const cancelLabel = t5 === undefined ? "Cancelar" : t5;
      return new Promise(resolve => {
        $resolve.current = resolve;
        setTypedValue("");
        setCopied(false);
        setState({
          title,
          description,
          confirmLabel,
          cancelLabel,
          variant,
          confirmText
        });
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const confirm = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = result => {
      setState(null);
      setTypedValue("");
      setCopied(false);
      $resolve.current?.(result);
      $resolve.current = null;
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const settle = t2;
  let t3;
  if ($[2] !== copyToClipboard || $[3] !== state) {
    t3 = () => {
      if (!state?.confirmText) {
        return;
      }
      copyToClipboard(state.confirmText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    $[2] = copyToClipboard;
    $[3] = state;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleCopyConfirmText = t3;
  const requiresTyping = !!state?.confirmText;
  let t4;
  if ($[5] !== requiresTyping || $[6] !== state?.confirmText || $[7] !== typedValue) {
    t4 = requiresTyping && typedValue.trim().toLowerCase() !== state?.confirmText.trim().toLowerCase();
    $[5] = requiresTyping;
    $[6] = state?.confirmText;
    $[7] = typedValue;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const isConfirmDisabled = t4;
  const t5 = !!state;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = open => !open && settle(false);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = state?.title;
  let t8;
  if ($[10] !== t7) {
    t8 = <ResponsiveAlertDialogTitle>{t7}</ResponsiveAlertDialogTitle>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== state) {
    t9 = state?.description && <ResponsiveAlertDialogDescription>{state.description}</ResponsiveAlertDialogDescription>;
    $[12] = state;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <ResponsiveAlertDialogHeader>{t8}{t9}</ResponsiveAlertDialogHeader>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== copied || $[18] !== handleCopyConfirmText || $[19] !== isConfirmDisabled || $[20] !== requiresTyping || $[21] !== state || $[22] !== typedValue) {
    t11 = requiresTyping && <ResponsiveAlertDialogBody><Field><FieldLabel htmlFor="confirm-type-to-confirm" className="block w-full">Escribe{" "}<span role="button" tabIndex={0} onClick={handleCopyConfirmText} onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleCopyConfirmText();
            }
          }} className="cursor-pointer font-semibold underline decoration-dotted underline-offset-2 hover:text-primary">{state.confirmText}{copied ? <CheckIcon className="ml-1 inline size-3.5 align-middle text-primary" /> : <CopyIcon className="ml-1 inline size-3.5 align-middle" />}</span>{" "}para confirmar</FieldLabel><Input id="confirm-type-to-confirm" autoFocus={true} autoComplete="off" value={typedValue} onChange={event_0 => setTypedValue(event_0.target.value)} onKeyDown={event_1 => {
          if (event_1.key === "Enter" && !isConfirmDisabled) {
            settle(true);
          }
        }} /></Field></ResponsiveAlertDialogBody>;
    $[17] = copied;
    $[18] = handleCopyConfirmText;
    $[19] = isConfirmDisabled;
    $[20] = requiresTyping;
    $[21] = state;
    $[22] = typedValue;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== state) {
    t12 = state?.cancelLabel && <ResponsiveAlertDialogCancel onClick={() => settle(false)}>{state.cancelLabel}</ResponsiveAlertDialogCancel>;
    $[24] = state;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = state?.variant;
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => settle(true);
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = state?.confirmLabel;
  let t16;
  if ($[27] !== isConfirmDisabled || $[28] !== t13 || $[29] !== t15) {
    t16 = <ResponsiveAlertDialogAction variant={t13} disabled={isConfirmDisabled} onClick={t14}>{t15}</ResponsiveAlertDialogAction>;
    $[27] = isConfirmDisabled;
    $[28] = t13;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t12 || $[32] !== t16) {
    t17 = <ResponsiveAlertDialogFooter>{t12}{t16}</ResponsiveAlertDialogFooter>;
    $[31] = t12;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t17) {
    t18 = <ResponsiveAlertDialogContent data-block="ConfirmDialog">{t10}{t11}{t17}</ResponsiveAlertDialogContent>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18 || $[39] !== t5) {
    t19 = <ResponsiveAlertDialog open={t5} onOpenChange={t6}>{t18}</ResponsiveAlertDialog>;
    $[38] = t18;
    $[39] = t5;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== children || $[42] !== t19) {
    t20 = <ConfirmContext.Provider value={confirm}>{children}{t19}</ConfirmContext.Provider>;
    $[41] = children;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  return t20;
};